import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const PrivacyPolicyContent = () => {
    const [contactEmail, setContactEmail] = useState('info@anotherhome.us');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/content/settings');
                if (response.data.success && response.data.data.contact_email) {
                    setContactEmail(response.data.data.contact_email);
                }
            } catch (error) {
                console.error("Error loading contact email", error);
            }
        };
        fetchSettings();
    }, []);

    // Palette Colors Reference:
    // Dark Text/Headings: #1F2A33
    // Brand Accent: #E84C5B
    // Light Backgrounds: #F6F4F1
    // Border/Muted: #D6D1CB

    return (
        <div className="pt-32 pb-24 px-6 max-w-5xl mx-auto animate-fade-in font-sans text-[#1F2A33]">

            {/* Header */}
            <header className="mb-16 border-b border-[#D6D1CB] pb-10">
                <span className="text-[#E84C5B] font-bold tracking-widest uppercase text-sm mb-4 block">
                    Legal & Compliance
                </span>
                <h1 className="text-5xl md:text-6xl font-serif font-bold text-[#1F2A33] mb-6 leading-tight">
                    Privacy Policy
                </h1>
                <p className="text-lg text-gray-500 italic">
                    Last updated: January 26, 2026
                </p>
            </header>

            {/* Content - Prose XL per testo più grande */}
            <div className="prose prose-xl prose-slate max-w-none text-[#1F2A33]/80 leading-relaxed">

                {/* Intro */}
                <section className="mb-14">
                    <p className="text-xl md:text-2xl leading-relaxed font-light text-[#1F2A33]">
                        <strong>Another Home</strong> ("we," "us," or "our") respects your privacy and is committed to protecting the personal information you share with us.
                        This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website
                        and use our hospitality services.
                    </p>
                    <p>
                        By accessing or using our Service, you signify that you have read, understood, and agree to our collection, storage, use,
                        and disclosure of your personal information as described in this Privacy Policy.
                    </p>
                </section>

                {/* 1. Info We Collect */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-8">1. Information We Collect</h2>

                    <h3 className="text-2xl font-bold text-[#1F2A33] mt-8 mb-4">Personal Information</h3>
                    <p>
                        We may collect personal information that you voluntarily provide to us when you register on the website,
                        express an interest in obtaining information about us or our products and services, participate in activities on the website,
                        or otherwise contact us. This includes:
                    </p>
                    <ul className="list-disc pl-6 space-y-3 marker:text-[#E84C5B]">
                        <li><strong>Identity Data:</strong> Full name, date of birth, and government-issued ID (for booking verification).</li>
                        <li><strong>Contact Data:</strong> Email address, phone number, and billing address.</li>
                        <li><strong>Financial Data:</strong> Payment card details (processed securely via third-party payment processors like Stripe).</li>
                        <li><strong>Account Data:</strong> Username, password (encrypted), and booking history.</li>
                    </ul>

                    <h3 className="text-2xl font-bold text-[#1F2A33] mt-8 mb-4">Automatically Collected Information</h3>
                    <p>
                        When you visit our website, our servers automatically collect certain information ("Usage Data"). This may include:
                    </p>
                    <ul className="list-disc pl-6 space-y-3 marker:text-[#E84C5B]">
                        <li>IP address, browser type, operating system, and device information.</li>
                        <li>Pages viewed, time spent on pages, and referring website addresses.</li>
                        <li>Geolocation data (to provide location-based services).</li>
                    </ul>
                </section>

                {/* 2. How We Use */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-8">2. How We Use Your Information</h2>
                    <p>We use the information we collect for business purposes, including to:</p>
                    <ul className="list-disc pl-6 space-y-3 marker:text-[#E84C5B]">
                        <li>Facilitate account creation, authentication, and management.</li>
                        <li>Process reservations, bookings, and payments securely.</li>
                        <li>Communicate with you regarding your stay, including confirmations and updates.</li>
                        <li>Provide customer support and respond to inquiries.</li>
                        <li>Send administrative information, such as changes to our terms, conditions, and policies.</li>
                        <li>Protect against fraud and ensure the security of our platform.</li>
                        <li>Comply with legal obligations and enforce our terms of service.</li>
                    </ul>
                </section>

                {/* 3. Sharing */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-8">3. Sharing Your Information</h2>
                    <p>
                        We do not sell, trade, or rent your personal identification information to others.
                        We may share information in the following situations:
                    </p>
                    <ul className="list-disc pl-6 space-y-3 marker:text-[#E84C5B]">
                        <li><strong>Service Providers:</strong> We may share data with third-party vendors, service providers, contractors, or agents who perform services for us (e.g., payment processing, hosting, email delivery).</li>
                        <li><strong>Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.</li>
                        <li><strong>Legal Requirements:</strong> We may disclose your information where we are legally required to do so to comply with applicable law, governmental requests, a judicial proceeding, court order, or legal process.</li>
                    </ul>
                </section>

                {/* 4. Security */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-8">4. Data Security</h2>
                    <p>
                        We use administrative, technical, and physical security measures to help protect your personal information.
                        These measures include secure JWT authentication, password hashing, and encrypted data transmission (SSL/TLS).
                        While we have taken reasonable steps to secure the personal information you provide to us, please be aware that no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
                    </p>
                </section>

                {/* 5. Retention */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-8">5. Data Retention</h2>
                    <p>
                        We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Policy,
                        unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements).
                    </p>
                </section>

                {/* 6. US Rights */}
                <section className="mb-14 bg-[#F6F4F1] p-8 md:p-10 rounded-[2rem] border border-[#D6D1CB]">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-6">6. Your Privacy Rights (USA)</h2>
                    <p className="mb-6">
                        Depending on your location, you may have certain rights regarding your personal information.
                    </p>
                    <h3 className="text-2xl font-bold text-[#1F2A33] mt-4 mb-3">California Residents (CCPA/CPRA)</h3>
                    <p className="mb-4">
                        If you are a resident of California, you have the right to:
                    </p>
                    <ul className="list-disc pl-6 space-y-3 marker:text-[#E84C5B] mb-6">
                        <li>Request access to the personal information we maintain about you.</li>
                        <li>Request deletion of your personal information, subject to certain exceptions.</li>
                        <li>Opt-out of the "sale" or "sharing" of your personal information (though we do not sell data).</li>
                        <li>Not receive discriminatory treatment for exercising your privacy rights.</li>
                    </ul>
                    <p>
                        To exercise these rights, please contact us at <a href={`mailto:${contactEmail}`} className="text-[#E84C5B] font-bold hover:underline">{contactEmail}</a>.
                    </p>
                </section>

                {/* 7. Children */}
                <section className="mb-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-8">7. Children's Privacy</h2>
                    <p>
                        Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13.
                        If we become aware that we have collected Personal Data from a child under age 13 without verification of parental consent, we take steps to remove that information from our servers.
                    </p>
                </section>

                {/* Contact */}
                <section className="border-t border-[#D6D1CB] pt-14 mt-14">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#1F2A33] mb-8">8. Contact Us</h2>
                    <p className="mb-6 text-xl">
                        If you have questions or comments about this policy, you may email us at:
                    </p>
                    <div className="bg-[#1F2A33] text-white p-8 md:p-10 rounded-[2rem] inline-block w-full md:w-auto min-w-[350px] shadow-xl">
                        <p className="font-bold text-2xl mb-2 font-serif">Another Home</p>
                        <p className="text-[#D6D1CB] mb-6 text-sm uppercase tracking-wider">Privacy Compliance Dept.</p>
                        <a href={`mailto:${contactEmail}`} className="text-[#E84C5B] font-bold text-2xl hover:text-white transition-colors flex items-center gap-2">
                            {contactEmail}
                        </a>
                    </div>
                </section>

            </div>
        </div>
    );
};

export default PrivacyPolicyContent;